import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

// The server returns 200 as soon as the pull is queued, so the immediate
// invalidation refetches rows that have not landed yet. These re-reads cover
// the window in which the background job commits.
const FOLLOW_UP_DELAYS_MS = [5_000, 15_000, 30_000];

/**
 * Queues a background Great Plains division pull. The invalidation only
 * guarantees the next division read is not served from cache — the rows
 * themselves land whenever the job finishes.
 */
export function useSyncDivisions() {
  const qc = useQueryClient();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => clearTimers, []);

  return useMutation({
    mutationFn: () => organizationApiClient.syncDivisions(),
    meta: { suppressErrorToast: true, errorContext: 'sync divisions' },
    onSuccess: () => {
      clearTimers();
      timersRef.current = FOLLOW_UP_DELAYS_MS.map((delay) =>
        setTimeout(() => invalidateFor('divisions.sync', qc), delay),
      );
    },
    onSettled: () => invalidateFor('divisions.sync', qc),
  });
}
